import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  dismissUndoToast,
  undoBatch,
  undoRequestByType,
  UNDO_WINDOW_MS,
} from 'store/modules/undo_toast_store/actions';
import {
  UndoBatchType,
  UndoResult,
  UndoToastPayload,
  UndoToastState,
} from 'store/modules/undo_toast_store/types';
import { getBiddings } from 'store/modules/bidding_store/actions';
import { ManagementState } from 'store/modules/management_store/types';
import { OnlineSearchState } from 'store/modules/online_search_store/types';
import { ActivitiesState } from 'store/modules/activities_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import tabId from 'services/tabId';

import * as S from './styles';

const TICK_INTERVAL_MS = 200;
const RESULT_DISMISS_MS = 4000;

const buildResultMessage = (result: UndoResult, type: UndoBatchType): string | null => {
  const { noun } = undoRequestByType[type];
  const ending = noun.feminine ? 'a' : 'o';

  if (result.kind === 'expired') return 'O prazo para desfazer terminou.';
  if (result.kind === 'nothing')
    return 'Nada foi desfeito: os itens foram alterados depois da ação.';
  if (result.kind === 'error') return 'Não foi possível desfazer. Tente novamente.';
  if (result.kind !== 'partial') return null;

  const kept = result.total - result.restored;
  const keptText =
    kept === 1
      ? `1 foi alterad${ending} depois e foi mantid${ending}.`
      : `${kept} foram alterad${ending}s depois e foram mantid${ending}s.`;
  return `${result.restored} de ${result.total} ${noun.plural} restaurad${ending}s. ${keptText}`;
};

const UndoToast: React.FC = () => {
  const dispatch = useDispatch();
  const { payload, request, result } = useSelector<ApplicationState, UndoToastState>(
    (state) => state.session.undoToast,
  );
  const { management } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );
  const { onlineSearch } = useSelector<ApplicationState, OnlineSearchState>(
    (state) => state.session.onlineSearch,
  );
  const { activities } = useSelector<ApplicationState, ActivitiesState>(
    (state) => state.storage.activities,
  );
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [now, setNow] = useState<number>(Date.now());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wasLoadingRef = useRef<boolean>(false);
  const pendingPayloadRef = useRef<UndoToastPayload | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  if (!portalRef.current) portalRef.current = document.createElement('div');

  useEffect(() => {
    const container = portalRef.current as HTMLDivElement;
    document.body.appendChild(container);
    return () => container.remove();
  }, []);

  // O modal fica no z-index máximo, e o empate é decidido pela ordem no body: o aviso precisa ser o
  // último filho para ficar acima de qualquer modal aberto.
  useEffect(() => {
    if (!payload) return undefined;
    const container = portalRef.current as HTMLDivElement;
    const keepLast = () => {
      if (document.body.lastElementChild !== container) document.body.appendChild(container);
    };
    keepLast();
    const observer = new MutationObserver(keepLast);
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [!!payload]);

  useEffect(() => {
    if (!payload) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return undefined;
    }

    setNow(Date.now());
    tickRef.current = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);

    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [payload?.expiresAt]);

  useEffect(() => {
    if (!payload) return;
    if (now >= payload.expiresAt) {
      dispatch(dismissUndoToast());
    }
  }, [now, payload?.expiresAt]);

  const refetchAfterUndo = (snapshot: UndoToastPayload) => {
    const { type, companyId } = snapshot;
    if (type === 'favorites') {
      const currentCompanyId = profile?.currentCompany?.[tabId()] || companyId;
      dispatch(
        getBiddings(
          {
            activities_list: activities?.[currentCompanyId],
            page: onlineSearch?.currentPage || 1,
            per_page: onlineSearch?.postsPerPage,
            favorite_scope: management?.filtered_by,
            favorite: true,
            archived: false,
            managed: false,
            order: management?.ordered_by,
            order_direction: management?.order_direction,
          },
          currentCompanyId,
        ),
      );
    }
  };

  useEffect(() => {
    if (request.loading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;

    if (request.loaded && pendingPayloadRef.current) {
      refetchAfterUndo(pendingPayloadRef.current);
      pendingPayloadRef.current = null;
    }
  }, [request.loading, request.loaded]);

  useEffect(() => {
    if (!result || result.kind === 'restored' || result.kind === 'error') return undefined;
    const timeoutId = setTimeout(() => dispatch(dismissUndoToast()), RESULT_DISMISS_MS);
    return () => clearTimeout(timeoutId);
  }, [result?.kind]);

  const remainingPercent = useMemo(() => {
    if (!payload) return 0;
    const remaining = Math.max(0, payload.expiresAt - now);
    return (remaining / UNDO_WINDOW_MS) * 100;
  }, [now, payload?.expiresAt]);

  if (!payload) return null;

  const resultMessage = result ? buildResultMessage(result, payload.type) : null;
  const showUndoButton = !result || result.kind === 'error';

  const handleUndo = () => {
    pendingPayloadRef.current = payload;
    dispatch(undoBatch(payload));
  };

  const handleDismiss = () => {
    dispatch(dismissUndoToast());
  };

  return ReactDOM.createPortal(
    <S.Container role="status" aria-live="polite">
      <S.Row>
        <S.Message>{resultMessage || payload.message}</S.Message>
        <S.Actions>
          {showUndoButton && (
            <S.UndoButton type="button" onClick={handleUndo} disabled={request.loading}>
              {request.loading ? 'Desfazendo...' : 'Desfazer'}
            </S.UndoButton>
          )}
          <S.CloseButton type="button" onClick={handleDismiss} aria-label="Fechar notificação">
            ×
          </S.CloseButton>
        </S.Actions>
      </S.Row>
      <S.ProgressBar>
        <S.ProgressFill percent={remainingPercent} />
      </S.ProgressBar>
    </S.Container>,
    portalRef.current,
  );
};

export default UndoToast;
